import type { Message } from "@/features/agent";
import { type AssetFile, AssetManager, uploadAssetToCloud } from "@/features/asset";
import { INITIAL_STATE } from "./constants";
import { getProject, getProjectDirectory, mvProjectAssets, updateProject } from "./repository";
import { ChatState, type ProjectState } from "./types";

type Listener = () => void;

/** Global set to track pending uploads across ProjectManager instances */
const pendingUploads = new Set<string>();

type SyncMessage =
  | {
      id: string;
      type: "project";
      patch: Partial<ProjectState>;
    }
  | {
      id: string;
      type: "message";
      patch: Partial<Message>;
    };

export class ProjectManager {
  public id = crypto.randomUUID();
  private state: ProjectState;
  public assetManager: AssetManager;
  private listeners: Set<Listener> = new Set();
  private dir?: FileSystemDirectoryHandle;
  private channel = new BroadcastChannel("gliakinetics/project");
  private initialized = false;
  public isOffscreen = false;

  constructor(
    public userId: string,
    public projectId?: string,
  ) {
    console.log("create manager", this.id);
    this.assetManager = new AssetManager();
    this.state = INITIAL_STATE;
    this.channel.onmessage = (e: MessageEvent<SyncMessage>) => {
      if (e.data.type === "project" && e.data.id === this.projectId) {
        this.state = { ...this.state, ...e.data.patch };
        this.notify();
      }
      if (e.data.type === "message") {
        const messageIndex = this.state.messages.findIndex((m) => m.id === e.data.id);
        if (messageIndex > -1) {
          const message = { ...this.state.messages[messageIndex], ...e.data.patch };
          const messages = [...this.state.messages];
          messages[messageIndex] = message;
          this.state = { ...this.state, messages };
          this.notify();
        }
      }
    };
  }

  /** Initialize the manager by loading project data from IndexedDB
   * @returns true on success, false if project not found
   */
  async init() {
    if (this.initialized) {
      console.error("ProjectManager is already initialized");
      return true;
    }
    this.initialized = true;

    this.dir = await getProjectDirectory(this.projectId || "tmp");
    this.assetManager.setDirectory(this.dir);

    if (!this.projectId) return true;

    const project = await getProject(this.userId, this.projectId);
    if (!project) {
      return false;
    }
    this.state = { ...this.state, ...project };
    this.notify();

    const tmpDir = await getProjectDirectory("tmp");
    await mvProjectAssets(tmpDir, this.dir, this.state.files.assets);

    await this.assetManager.load(this.state.files.assets);
    return true;
  }

  getState = () => this.state;
  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  updateState = (partial: Partial<ProjectState>) => {
    this.state = { ...this.state, ...partial };
    if (this.projectId) {
      this.channel.postMessage({
        id: this.projectId,
        type: "project",
        patch: partial,
      } satisfies SyncMessage);
    }
    this.syncToStorage();
    this.notify();
  };
  updateFiles = (files: Partial<ProjectState["files"]>) => {
    this.updateState({ files: { ...this.state.files, ...files } });
  };
  updateMessage = (messageId: string, message: Partial<Message>) => {
    const messages = this.state.messages.map((m) =>
      m.id === messageId ? { ...m, ...message } : m,
    );
    this.state = { ...this.state, messages };
    this.channel.postMessage({
      id: messageId,
      type: "message",
      patch: message,
    } satisfies SyncMessage);
    this.syncToStorage();
    this.notify();
  };
  toggleTaskApproval = (messageId: string, index: number) => {
    const message = this.state.messages.find((m) => m.id === messageId);
    if (!message || !message.checkpoint) return;
    const checkpoint = message.checkpoint.map((task, i) =>
      i === index ? { ...task, approved: !task.approved } : task,
    );
    this.updateMessage(messageId, { checkpoint });
  };
  setCheckpointApproval = (messageId: string, value: boolean) => {
    const message = this.state.messages.find((m) => m.id === messageId);
    if (!message || !message.checkpoint) return;
    const checkpoint = message.checkpoint.map((task) => ({ ...task, approved: value }));
    this.updateMessage(messageId, { checkpoint });
  };
  saveSnapshot = (messageId: string) => {
    const snapshot = {
      files: this.state.files,
      duration: this.state.duration,
      data: this.state.data,
    };
    this.updateMessage(messageId, { snapshot });
    return snapshot;
  };

  pushAssets = (...newAssets: AssetFile[]) => {
    const assets = [...this.state.files.assets, ...newAssets];
    this.updateFiles({ assets });
  };
  deleteAsset = (assetId: string) => {
    const assets = this.state.files.assets.filter((a) => a.id !== assetId);
    this.updateFiles({ assets });
  };
  renameAsset = (assetId: string, newName: string) => {
    const assets = this.state.files.assets.map((a) =>
      a.id === assetId ? { ...a, name: newName } : a,
    );
    this.updateFiles({ assets });
  };

  /**
   * Uploads assets without URLs to cloud in background.
   * Updates asset URLs in state after successful upload.
   * Deduplicates concurrent uploads for the same asset.
   * Non-blocking; errors are logged but don't throw.
   */
  uploadAssetsToCloud(assets?: AssetFile[]) {
    const assetsToUpload = (assets ?? this.state.files.assets).filter((a) => !a.url);
    for (const asset of assetsToUpload) {
      // Skip if already uploading
      if (pendingUploads.has(asset.id)) continue;
      pendingUploads.add(asset.id);

      this.assetManager
        .getFile(asset.id)
        .then((blob) => {
          if (!blob) return;
          return uploadAssetToCloud(asset, blob);
        })
        .then((publicUrl) => {
          if (!publicUrl) return;
          const updatedAssets = this.state.files.assets.map((a) =>
            a.id === asset.id ? { ...a, url: publicUrl } : a,
          );
          this.updateFiles({ assets: updatedAssets });
        })
        .catch((error) => {
          // Cloud upload failure is non-fatal; OPFS copy remains available
          console.warn(`Cloud upload failed for asset ${asset.id}:`, error);
        })
        .finally(() => {
          pendingUploads.delete(asset.id);
        });
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private syncToStorage(): void {
    if (!this.projectId) return;
    updateProject(this.userId, this.projectId, this.state);
  }

  /** Clean up resources when the state is idle and is offscreen, returns true if disposed */
  tryDispose() {
    if (this.state.chatState === ChatState.IDLE && this.isOffscreen) {
      console.log("dispose manager", this.id);
      this.channel.close();
      this.listeners.clear();
      return true;
    }
    return false;
  }
}
